import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SmartDevice {
  id: string;
  provider_id: string;
  device_name: string;
  device_id: string;
  is_enabled: boolean;
  is_connected: boolean;
  last_connection_check: string | null;
  preheat_minutes: number | null;
  created_at: string;
  updated_at: string;
}

export const useSmartDevices = (providerId?: string) => {
  return useQuery({
    queryKey: ['smart_devices', providerId],
    queryFn: async () => {
      if (!providerId) return [];
      
      const { data, error } = await supabase
        .from('smart_devices')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SmartDevice[];
    },
    enabled: !!providerId,
  });
};

export const useCheckConnection = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (device: SmartDevice) => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await supabase.functions.invoke('check-device-connection', {
          body: { 
            deviceId: device.device_id,
            smartDeviceId: device.id 
          },
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        });

        if (response.error) throw response.error;

        return { 
          isConnected: response.data.isConnected, 
          deviceId: device.id 
        };
      } catch (error) {
        console.error('Connection check failed:', error);
        return { isConnected: false, deviceId: device.id };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['smart_devices'] });
      toast({
        title: data.isConnected ? "Device Connected" : "Connection Failed",
        description: data.isConnected 
          ? "Device is online and responding." 
          : "Could not connect to device. Please check the Device ID.",
        variant: data.isConnected ? "default" : "destructive",
      });
    },
  });
};

export const useCreateSmartDevice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (device: Omit<SmartDevice, 'id' | 'created_at' | 'updated_at' | 'is_connected' | 'last_connection_check'>) => {
      const { data, error } = await supabase
        .from('smart_devices')
        .insert({
          provider_id: device.provider_id,
          device_name: device.device_name,
          device_id: device.device_id,
          is_enabled: device.is_enabled,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['smart_devices', data.provider_id] });
      toast({
        title: "Device Added",
        description: `${data.device_name} has been added successfully.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to add device.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateSmartDevice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SmartDevice> & { id: string }) => {
      const { data, error } = await supabase
        .from('smart_devices')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['smart_devices', data.provider_id] });
      const statusMessage = data.is_enabled ? 'enabled' : 'disabled';
      toast({
        title: "Device Updated",
        description: `${data.device_name} has been ${statusMessage}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update device.",
        variant: "destructive",
      });
    },
  });
};

export const useManualSaunaControl = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ device, action }: { device: SmartDevice; action: 'on' | 'off' }) => {
      const { data: { session } } = await supabase.auth.getSession();

      const response = await supabase.functions.invoke('manual-sauna-control', {
        body: {
          deviceId: device.device_id,
          smartDeviceId: device.id,
          action,
        },
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['smart_device_logs'] });
      const label = variables.action === 'on' ? 'Turn On' : 'Turn Off';
      toast({
        title: data.success ? `${label} Successful` : `${label} Failed`,
        description: data.success
          ? `Sauna ${variables.action === 'on' ? 'turned on' : 'turned off'} successfully.`
          : data.errorMessage || data.responseMessage || 'Device did not respond.',
        variant: data.success ? 'default' : 'destructive',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to control sauna.',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteSmartDevice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('smart_devices')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smart_devices'] });
      toast({
        title: "Device Deleted",
        description: "Device has been removed successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete device.",
        variant: "destructive",
      });
    },
  });
};
